import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
// Modal portal handled by reusable component now
import * as d3 from "d3";
import moment from "moment";
import { generateLabels, generateDailyLabels } from "./chartHelpers";
import {
  buildYDomain,
  makeScales,
  createAxes,
  buildLegend,
  legendHTML,
  createSimplifiedPaths,
  attachRefine,
  createTooltip,
  createCrosshair,
  computeSignature,
} from './useTimeSeriesInternals';
import "./style.scss";
import FullscreenModal from '../common/FullscreenModal/FullscreenModal';
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { sideMenu } from "../../utils/atoms/sideMenuAtom";
import { useAtom } from "jotai";
import { useMantineColorScheme } from "@mantine/core";
import ChartWidthAdjuster from './ChartWidthAdjuster';
import { useTranslation } from 'react-i18next';



// TimeSeriesGraph renders a time series chart with optional dual series (e.g., electrical and thermal)
const TimeSeriesGraph = ({
  dataAssset,      // Array of numbers for the main (electrical) series
  dataAssset2,     // Array of numbers for the secondary (thermal) series
  name,            // Name of the chart (e.g., 'Ashrae')
  yAxisName,       // Label for the Y axis
  chartStartDate,  // Start date for the chart labels
  fullViewport = false,   // If true, chart fills the viewport
  hideTypeToggle = false, // If true, hides the line/bar toggle button
  showFullscreenButton = true, // If true, shows the fullscreen button
  onFullscreen,           // Callback for fullscreen toggle
  embedHeight = 360,      // Height of the embedded chart
  showMinimap: showMinimapProp = true, // If true, shows the minimap
  minimapGap = 18,        // Gap between main chart and minimap
  deferUntilVisible = true, // Wait until container is visible before drawing
  inModalInstance = false,  // Internal flag for modal fullscreen
  interval,                // Interval in minutes between data points
  onStats,                  // Optional callback(stats) -> receives computed stats
  onUnitChange,             // Optional callback(unit) -> called when W/kW unit is toggled
  startTime = "00:00",  // Optional start time for the data series (HH:mm)
  legendLabels: legendLabelsProp,
  tooltipLabels,
  dailyMode = false,
}) => {
  const { t, i18n } = useTranslation();
  // State for side menu collapse and color scheme
  const [collapseSideMenu] = useAtom(collapseAtom);
  const [sideMenuShow] = useAtom(sideMenu);
  const { colorScheme } = useMantineColorScheme();
  const timeLocale = useMemo(() => {
    const lang = (i18n.language || "").toLowerCase();
    if (lang.startsWith("fr")) {
      return d3.timeFormatLocale({
        dateTime: "%A %e %B %Y %X",
        date: "%d/%m/%Y",
        time: "%H:%M:%S",
        periods: ["AM", "PM"],
        days: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
        shortDays: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
        months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
        shortMonths: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."]
      });
    }
    return d3.timeFormatLocale({
      dateTime: "%A, %B %e, %Y, %X",
      date: "%m/%d/%Y",
      time: "%H:%M:%S",
      periods: ["AM", "PM"],
      days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      shortMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    });
  }, [i18n.language]);
  // Adaptive tick formatter: show time when zoomed closely, day/month/year when zoomed out
  const xTickFormatter = useMemo(() => {
    const formatMillisecond = timeLocale.format(".%L");
    const formatSecond = timeLocale.format(":%S");
    const formatMinute = timeLocale.format("%H:%M");
    const formatHour = timeLocale.format("%H:%M");
    const formatDay = timeLocale.format("%a %d");
    const formatWeek = timeLocale.format("%b %d");
    const formatMonth = timeLocale.format("%b");
    const formatYear = timeLocale.format("%Y");
    const formatTimeOnly = timeLocale.format("%H:%M");
    if (dailyMode) {
      // Daily chart: always show HH:MM; last tick (midnight of next day) shows as "24:00"
      const ONE_DAY = 24 * 60 * 60 * 1000;
      const base = chartStartDate ? new Date(chartStartDate) : new Date(new Date().getFullYear() - 1, 0, 1);
      const dayStart = new Date(base);
      dayStart.setHours(0, 0, 0, 0);
      const dayEndMs = dayStart.getTime() + ONE_DAY;
      return (date) => date.getTime() >= dayEndMs ? "24:00" : formatTimeOnly(date);
    }
    return (date) => {
      if (d3.timeSecond(date) < date) return formatMillisecond(date);
      if (d3.timeMinute(date) < date) return formatSecond(date);
      if (d3.timeHour(date) < date) return formatMinute(date);
      if (d3.timeDay(date) < date) return formatHour(date);
      if (d3.timeMonth(date) < date) {
        return d3.timeWeek(date) < date ? formatDay(date) : formatWeek(date);
      }
      return d3.timeYear(date) < date ? formatMonth(date) : formatYear(date);
    };
  }, [timeLocale, dailyMode, chartStartDate]);

  // Memoized data arrays for performance
  const memorizedDataAsset = useMemo(() => dataAssset || [], [dataAssset]);
  const memorizedDataAsset2 = useMemo(() => dataAssset2 || [], [dataAssset2]);

  // Clean up chart on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.innerHTML = "";
      }
      // Clean up chart internals refs
      if (chartInternalsRef && chartInternalsRef.current) {
        Object.keys(chartInternalsRef.current).forEach((key) => {
          chartInternalsRef.current[key] = null;
        });
      }
      // Clean up drawChartRef
      if (drawChartRef) {
        drawChartRef.current = () => {};
      }
    };
  }, []);
  const chartRef = useRef(null);
  const [chartDisplay, setChartDisplay] = useState("block");
  const [isLoading, setIsLoading] = useState(false); // Only used on first draw
  const [chartType, setChartType] = useState("line"); // 'line' or 'bar'
  const [isFullscreen, setIsFullscreen] = useState(false); // Inline fullscreen (legacy)
  const [fullscreenModal, setFullscreenModal] = useState(false); // Modal fullscreen
  // Unit toggle state (W / kW) - initializes from provided yAxisName if present
  const [powerUnit, setPowerUnit] = useState(() => (yAxisName || "W"));
  useEffect(() => {
    // keep initial prop-driven unit, but allow user to override after mount
    if (yAxisName && yAxisName !== powerUnit) setPowerUnit(yAxisName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yAxisName]);
  // Effective display Y axis label (reflects unit selection when applicable)
  const effectiveYAxisName = powerUnit || yAxisName || "";
  // Series visibility toggles
  const [showElectrical, setShowElectrical] = useState(true);   // Show/hide electrical
  const [showThermal, setShowThermal] = useState(true); // Show/hide thermal
  // Layout transition state
  const [layoutTransitioning, setLayoutTransitioning] = useState(false);

  // Scaling factors (reserved for future UI controls)
  const [electricalScale] = useState(0);
  const [thermalScale] = useState(0);

  // Format a value for display (no scientific notation, always readable)
  const formatValue = (value) => {
    if (value == null || isNaN(value)) return '0';
    const num = Number(value);
    const abs = Math.abs(num);
    if (abs >= 1) {
      // Show as integer if no decimals, else up to 2 decimals
      if (Number.isInteger(num)) {
        return d3.format(",d")(num); // e.g., 12,345
      } else {
        return d3.format(",.2f")(num).replace(/\.00$/, "").replace(/(\.[1-9]*)0+$/, "$1");
      }
    }
    if (abs >= 1e-3) {
      // Between 0.001 and 1 -> up to 4 decimals, trim trailing zeros without regex
      let str = d3.format(".2f")(num);
      while (str.endsWith('0')) str = str.slice(0, -1);
      if (str.endsWith('.')) str = str.slice(0, -1);
      return str === '' ? '0' : str;
    }
    // Very small -> avoid scientific; show 2 decimals then trim without regex
    let tiny = num.toFixed(2);
    while (tiny.endsWith('0')) tiny = tiny.slice(0, -1);
    if (tiny.endsWith('.')) tiny = tiny.slice(0, -1);
    return tiny === '' || tiny === '-0' ? '0' : tiny;
  };

  // calculateStats moved to helpers; only local formatValue override retained

  // PERFORMANCE: Cache yearly labels so we don't rebuild 35k timestamps repeatedly
  const labelsCacheRef = useRef({ key: null, labels: [] });
  // Track if we have already fully drawn (first heavy init)
  const initializedRef = useRef(false);
  // Track last drawn dimensions to avoid redundant redraw on minor/no size changes
  const lastDimsRef = useRef(null);
  // Removed scheduleRedraw & side menu responsive redraw to prevent extra drawChart calls during scroll.

  // Dynamic visibility state (deferUntilVisible only delays until element first becomes visible)
  const [isVisible, setIsVisible] = useState(!deferUntilVisible);
  useEffect(() => {
    if (!deferUntilVisible) return; // already visible / not deferring
    const el = chartRef.current;
    if (!el) return;
    let done = false;
    function markVisible() {
      if (done) return;
      done = true;
      setIsVisible(true);
    }
    // Prefer IntersectionObserver
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting || e.intersectionRatio > 0) {
            markVisible();
          }
        });
      }, { threshold: 0.01 });
      try { io.observe(el); } catch(_) {}
      // Fallback timeout safety
      const safety = setTimeout(() => markVisible(), 4000);
      return () => { io.disconnect(); clearTimeout(safety); };
    } else {
      // Polling fallback (rare older browsers)
      let frames = 0;
      const check = () => {
        if (!el.isConnected) return; // if unmounted stop
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight) {
          markVisible();
          return;
        }
        frames++;
        if (frames < 240) requestAnimationFrame(check); else markVisible();
      };
      requestAnimationFrame(check);
    }
  }, [deferUntilVisible]);


  const dataBundle = useMemo(() => {
    // Determine effective start date. For Ashrae charts, always use Jan 1 of previous calendar year.
    let effectiveStart = chartStartDate;
    if (name === 'PV Create Profile') {
      const now = new Date();
      effectiveStart = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0); // Jan 1 previous year
    }
    if (name === 'Ashrae') {
      effectiveStart = new Date(2019, 0, 1);
    }

    // Use helper for label generation and cache (pass startTime for the starting hour:minute)
    const { labels, key } = dailyMode
      ? generateDailyLabels(interval || 15, effectiveStart)
      : generateLabels(effectiveStart, interval || 15, startTime);
    if (labelsCacheRef.current.key !== key) {
      labelsCacheRef.current = { key, labels };
    }
    let data1 = memorizedDataAsset.slice();
    let data2 = memorizedDataAsset2.slice();
    if (name === 'Ashrae') {
      if (electricalScale) data1 = data1.map(v => v * (1 + parseFloat(electricalScale)));
      if (thermalScale) data2 = data2.map(v => v * (1 + parseFloat(thermalScale)));
    }
    return { labels, data1, data2 };
  }, [memorizedDataAsset, memorizedDataAsset2, name, electricalScale, thermalScale, chartStartDate, interval, startTime, dailyMode]);

  // Keep an external ref so effects don't depend on drawChart function identity
  const drawChartRef = useRef(() => {});
  // Store internal D3 objects so we can perform a width-only resize without a full redraw
  const chartInternalsRef = useRef(null);
  const drawChart = useCallback(() => {
    const rootEl = chartRef.current;
    if (!rootEl) return;
    // Clean up previous chart elements to free memory
    while (rootEl.firstChild) {
      rootEl.removeChild(rootEl.firstChild);
    }
    const { labels } = dataBundle;
    const nativeUnit = yAxisName || "W";
    let unitFactor = 1;
    if (nativeUnit === "W" && powerUnit === "kW") unitFactor = 1 / 1000;
    else if (nativeUnit === "kW" && powerUnit === "W") unitFactor = 1000;
    let data1 = (dataBundle.data1 || []).slice();
    let data2 = (dataBundle.data2 || []).slice();
    if (unitFactor !== 1) {
      data1 = data1.map((v) => (Number.isFinite(v) ? v * unitFactor : v));
      data2 = data2.map((v) => (Number.isFinite(v) ? v * unitFactor : v));
    }
    const effectiveYAxisName = powerUnit || yAxisName || "";
    if (!labels.length) return;

    const dark = colorScheme === "dark";
  const onlyThermal = !data1.length && !!data2.length; // single-series thermal fallback
  const hasDual = data1.length && data2.length;
    // Responsive / embedded sizing (make embedHeight the TOTAL container height for stability)
    const legendReserved = 34; // vertical pixels for legend + gap (consumed in margin.top)
    const autoShowMinimap = (showMinimapProp !== false) && ((isFullscreen || fullViewport) || embedHeight >= 360);
    const showMinimap = showMinimapProp && autoShowMinimap;
    const totalHeight = (isFullscreen || fullViewport) ? window.innerHeight : embedHeight;
  let miniHeight = showMinimap ? 55 : 0;
  if (totalHeight < 360 && showMinimap) miniHeight = 48;
  if (totalHeight < 320 && showMinimap) miniHeight = 42;
  const miniGap = showMinimap ? minimapGap : 0;
  const axisBand = 30; // space for main x-axis
  // Set left margin to 100px to fit large y-axis labels (up to 7 digits)
  const margin = { top: 12 + legendReserved, right: 20, bottom: axisBand + (showMinimap ? (miniGap + miniHeight) : 0), left: 80 };
    // Measure available width more robustly (use boundingClientRect and walk up if zero)
    function measureWidth(node) {
      if (!node) return 800;
      let w = node.getBoundingClientRect().width;
      if (w && w > 0) return w;
      if (node.parentElement) return measureWidth(node.parentElement);
      return 800;
    }
    const width = (isFullscreen || fullViewport) ? window.innerWidth : measureWidth(rootEl);
    // Plot height derived from total minus margins (legend accounted for in margin.top)
    let height = totalHeight - margin.top - margin.bottom;
    // if (showMinimap) height = height; // (clarity) height already excludes minimap via margin.bottom
    if (height < 150) height = 150; // enforce minimum plot height
    // Explicitly enforce container height early to avoid layout jump / overlap with tabs
    rootEl.style.height = `${totalHeight}px`;
    const innerWidth = width - margin.left - margin.right;

    // Toolbar
    const toolbar = d3.select(rootEl)
      .append("div")
      .style("position", "absolute")
      .style("top", "4px")
      .style("right", "8px")
      .style("display", "flex")
      .style("gap", "4px")
      .style("font-family", "Nunito, sans-serif")
      .style("z-index", 30)
      .style("pointer-events", "auto");
    let zoomBehavior = null; // reference later
    let overlayRef = null;
    const mkBtn = (label, title) => toolbar.append("button")
      .text(label)
      .attr("type", "button")
      .attr("title", title)
      .style("padding", "2px 6px")
      .style("font-size", "12px")
      .style("font-family", "Nunito, sans-serif")
      .style("cursor", "pointer")
      .style("border", "1px solid #777")
      .style("background", dark ? "#222" : "#fafafa")
      .style("color", dark ? "#eee" : "#222")
      .style("border-radius", "3px")
      .style("display", "flex")
      .style("align-items", "center")
      .style("justify-content", "center");
    if (showFullscreenButton && !inModalInstance) {
      mkBtn('⛶', t('timeSeries.toolbar.fullscreenOpen')).on('click', () => setFullscreenModal(true));
    } else if (showFullscreenButton && inModalInstance) {
      // In modal instance, show an exit button
      mkBtn('🗗', t('timeSeries.toolbar.fullscreenExit')).on('click', () => {
        if (typeof onFullscreen === 'function') onFullscreen();
      });
    }

    if (!hideTypeToggle) {
      mkBtn(chartType === 'line' ? t('timeSeries.toolbar.bars') : t('timeSeries.toolbar.lines'), t('timeSeries.toolbar.toggleLineBar'))
        .on('click', () => setChartType(chartType === 'line' ? 'bar' : 'line'));
    }
    const zoomInBtn = mkBtn('+', t('timeSeries.toolbar.zoomIn'));
    const zoomOutBtn = mkBtn('-', t('timeSeries.toolbar.zoomOut'));
    const resetBtn = mkBtn(t('timeSeries.toolbar.reset'), t('timeSeries.toolbar.reset'));

    // Download dropdown (PNG or CSV)
    const downloadSelect = toolbar.append("select")
      .attr("title", t('timeSeries.toolbar.download'))
      .style("padding", "2px 6px")
      .style("font-size", "12px")
      .style("font-family", "Nunito, sans-serif")
      .style("cursor", "pointer")
      .style("border", "1px solid #777")
      .style("background", dark ? "#222" : "#fafafa")
      .style("color", dark ? "#eee" : "#222")
      .style("border-radius", "3px");
    
    downloadSelect.append("option")
      .attr("value", "")
      .attr("disabled", true)
      .attr("selected", true)
      .attr("hidden", true)
      .text(t('timeSeries.toolbar.download'));
    downloadSelect.append("option")
      .attr("value", "png")
      .text("PNG");
    downloadSelect.append("option")
      .attr("value", "csv")
      .text("CSV");

    // Unit toggle dropdown (W / kW) - always visible
    const unitSelect = toolbar.append('select')
      .attr('title', t('timeSeries.toolbar.unitToggle') || 'Unit')
      .style('padding', '2px 6px')
      .style('font-size', '12px')
      .style('font-family', 'Nunito, sans-serif')
      .style('cursor', 'pointer')
      .style('border', '1px solid #777')
      .style('background', colorScheme === 'dark' ? '#222' : '#fafafa')
      .style('color', colorScheme === 'dark' ? '#eee' : '#222')
      .style('border-radius', '3px');

    unitSelect.append('option').attr('value', 'W').text('W');
    unitSelect.append('option').attr('value', 'kW').text('kW');
    try { unitSelect.property('value', powerUnit || 'W'); } catch (_) {}
    unitSelect.on('change', function () {
      const v = this.value;
      setPowerUnit(v);
      if (typeof onUnitChange === 'function') onUnitChange(v);
    });

  const svg = d3
      .select(rootEl)
      .append("svg")
      .attr("width", width)
  .attr("height", height + margin.top + margin.bottom) // should equal totalHeight
      .style("background", dark ? "#111111" : "#ffffff");

    // Download handler for PNG
    const downloadPNG = () => {
      try {
        const svgEl = svg.node();
        if (!svgEl) return;
        // Inline styles may rely on external CSS; serialize current computed styles minimally
        const cloned = svgEl.cloneNode(true);
        // Ensure background rect for consistent PNG (in case style isn't picked up)
        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bg.setAttribute('x', '0');
        bg.setAttribute('y', '0');
        bg.setAttribute('width', svgEl.getAttribute('width'));
        bg.setAttribute('height', svgEl.getAttribute('height'));
        bg.setAttribute('fill', dark ? '#111111' : '#ffffff');
        cloned.insertBefore(bg, cloned.firstChild);
        // Serialize
        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(cloned);
        // Add namespace if missing
        if (!svgString.match(/^<svg[^>]+xmlns=/)) {
          svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        // Convert to blob URL
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            // Fill background explicitly
            ctx.fillStyle = dark ? '#111111' : '#ffffff';
            ctx.fillRect(0,0,canvas.width,canvas.height);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            const pngUrl = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            const ts = new Date().toISOString().replace(/[:.]/g,'-');
            a.download = `${name || 'chart'}-${ts}.png`;
            a.href = pngUrl;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } catch (e) {
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => URL.revokeObjectURL(url);
        img.src = url;
      } catch (e) {
        // Silent fail
      }
    };

    // Download handler for CSV - exports datetime and value columns
    const downloadCSV = () => {
      try {
        const hasDualData = data1.length && data2.length;
        const rows = [];
        
        // Extract unit from yAxisName (e.g., "kW", "Rate ($)", "Rate (EUR)")
        const getUnit = () => {
          if (!effectiveYAxisName) return '';
          // Avoid regex on untrusted/very large input to prevent backtracking risks.
          const s = String(effectiveYAxisName);
          const open = s.indexOf('(');
          if (open >= 0) {
            const close = s.indexOf(')', open + 1);
            if (close > open) {
              const inside = s.slice(open + 1, close).trim();
              return inside.length > 32 ? inside.slice(0, 32) : inside;
            }
          }
          // Fallback: return whole label but cap length to avoid extremely large values
          const whole = s.trim();
          return whole.length > 32 ? whole.slice(0, 32) : whole;
        };
        
        // Determine column names dynamically based on chart name
        const getColumnName = () => {
          const chartName = (name || '').toLowerCase();
          if (chartName.includes('ashrae')) return 'Ashrae';
          if (chartName.includes('thermal')) return 'Thermal Load';
          if (chartName.includes('electrical')) return 'Electrical Load';
          return name || 'Value';
        };
        
        // Build CSV header with units
        const unit = getUnit();
        const unitSuffix = unit ? ` (${unit})` : '';
        
        if (hasDualData) {
          // For dual data, use Electrical Load and Thermal Load columns with units
          rows.push(`DateTime,Electrical Load (W),Thermal Load (W)`);
        } else if (data1.length) {
          // Single series (data1) - use dynamic column name with unit
          rows.push(`DateTime,${getColumnName()}${unitSuffix}`);
        } else if (data2.length) {
          // Single series (data2 - typically thermal) - use dynamic column name with unit
          const chartName = (name || '').toLowerCase();
          const colName = chartName.includes('thermal') ? 'Thermal Load' : getColumnName();
          rows.push(`DateTime,${colName}${unitSuffix}`);
        } else {
          return; // No data to export
        }
        // Build CSV rows - each label corresponds to a value
        const maxLen = Math.max(labels.length, data1.length, data2.length);
        for (let i = 0; i < maxLen; i++) {
          const dateLabel = labels[i] ? moment(labels[i]).format('YYYY-MM-DD HH:mm:ss') : '';
          if (hasDualData) {
            const val1 = data1[i] !== undefined ? data1[i] : '';
            const val2 = data2[i] !== undefined ? data2[i] : '';
            rows.push(`${dateLabel},${val1},${val2}`);
          } else if (data1.length) {
            const val = data1[i] !== undefined ? data1[i] : '';
            rows.push(`${dateLabel},${val}`);
          } else {
            const val = data2[i] !== undefined ? data2[i] : '';
            rows.push(`${dateLabel},${val}`);
          }
        }
        const csvContent = rows.join('\n');
        // Prepend UTF-8 BOM so Excel correctly renders Unicode chars (€, £, ¥, etc.)
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        a.download = `${name || 'chart'}-${ts}.csv`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        // Silent fail
      }
    };

    // Attach change handler to download dropdown
    downloadSelect.on('change', function() {
      const value = d3.select(this).property('value');
      if (value === 'png') {
        downloadPNG();
      } else if (value === 'csv') {
        downloadCSV();
      }
      // Reset dropdown back to "Download" placeholder
      d3.select(this).property('selectedIndex', 0);
    });

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Clip path to keep lines inside plotting area (inside axes)
    // Generate a robust unique clipId without relying on Math.random (use crypto when available)
    const clipId = (() => {
      const prefix = 'clip-';
      if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        const arr = new Uint32Array(2);
        window.crypto.getRandomValues(arr);
        return prefix + arr[0].toString(36) + arr[1].toString(36);
      }
      // Fallback: timestamp + incremental counter to avoid collisions within session
      const ts = Date.now().toString(36);
      if (!window.__clipCounter) window.__clipCounter = 0;
      const cnt = (window.__clipCounter++).toString(36);
      return prefix + ts + cnt;
    })();
    g.append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("width", innerWidth)
      .attr("height", height)
      .attr("x", 0)
      .attr("y", 0);

    const yDomain = buildYDomain({ name, data1, data2, showElectrical, showThermal });
    const { x, y } = makeScales(labels, yDomain, innerWidth, height);
    let xCurrent = x.copy();
    const axisTickFontSize = 13;
    const { xAxis, xAxisG, yAxisG } = createAxes(g, { x, y, height, dark, axisTickFontSize, xTickFormatter, dailyMode });
    // Ensure axis baseline and tick lines have explicit stroke so they survive serialization
    try {
      xAxisG.selectAll('.domain')
        .attr('stroke', dark ? '#ffffff' : '#222')
        .attr('stroke-width', 1);
      xAxisG.selectAll('.tick line')
        .attr('stroke', dark ? '#888' : '#999')
        .attr('stroke-width', 1);
      yAxisG.selectAll('.domain')
        .attr('stroke', dark ? '#ffffff' : '#222')
        .attr('stroke-width', 1);
      yAxisG.selectAll('.tick line')
        .attr('stroke', dark ? '#888' : '#999')
        .attr('stroke-width', 1);
    } catch(_) {}

    // Y-Axis Label (rotated)
    if (effectiveYAxisName) {
      g.append('text')
        .attr('class', 'y-axis-title')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        // Position left outside y-axis ticks; margin.left provides space
        .attr('y', - (margin.left - 16))
        .attr('text-anchor', 'middle')
        .attr('fill', dark ? '#eee' : '#222')
        .style('font-family', 'Nunito, sans-serif')
        .style('font-size', '14px')
        .text(effectiveYAxisName);
    }

    // Legend (stats for currently zoomed window). Dual-series -> only show name per original requirement
    const legend = buildLegend(rootEl, dark);
    const updateLegend = (currentScale) => {
      const legendLabels = {
        electricalLoad: t('result.timeSeries.electrical'),
        thermalLoad: t('result.timeSeries.thermal'),
        min: t('ashrae.min'),
        max: t('ashrae.max'),
        avg: t('ashrae.avg'),
        energy: t('ashrae.energy'),
        ...legendLabelsProp,
      };
      const html = legendHTML({
        dual: hasDual,
        name,
        showElectrical,
        showThermal,
        onlyThermal,
        data1,
        data2,
        labels,
        currentScale,
        dark,
        formatValue,
        intervalMinutesProp: interval,
        ashrae: name === 'Ashrae',
        yAxisName: effectiveYAxisName,
        legendLabels
      });
      legend.html(html);
      // Compute structured stats and emit via onStats if requested
      if (typeof onStats === 'function') {
        const bisectAll = d3.bisector(d => d);
        const domain = currentScale.domain();
        const startIndex = Math.min(labels.length - 1, Math.max(0, bisectAll.left(labels, domain[0])));
        const endIndex = Math.min(labels.length - 1, Math.max(startIndex, bisectAll.right(labels, domain[1]) - 1));
        // Determine interval minutes similar to legendHTML logic
        let intervalMinutesLocal = 0;
        const count = endIndex - startIndex + 1;
        if (count > 1) {
          const diffs = [];
          for (let i = startIndex; i < endIndex; i++) {
            const d = (labels[i + 1] - labels[i]) / 60000;
            if (d > 0 && isFinite(d)) diffs.push(d);
          }
          if (diffs.length) {
            diffs.sort((a,b) => a-b);
            intervalMinutesLocal = diffs[Math.floor(diffs.length / 2)];
          }
        }
        if (interval != null) {
          const parsed = parseFloat(interval);
          if (parsed > 0 && isFinite(parsed)) intervalMinutesLocal = parsed;
          else if (!intervalMinutesLocal || !isFinite(intervalMinutesLocal)) intervalMinutesLocal = 15;
        } else if (!intervalMinutesLocal || !isFinite(intervalMinutesLocal)) {
          intervalMinutesLocal = 15;
        }
        const intervalHours = intervalMinutesLocal / 60;
        const computeStats = (arr) => {
          let localMin = Infinity, localMax = -Infinity, sum = 0, n = 0;
          for (let i = startIndex; i <= endIndex; i++) {
            const v = arr[i];
            if (typeof v === 'number' && !isNaN(v)) {
              if (v < localMin) localMin = v;
              if (v > localMax) localMax = v;
              sum += v;
              n++;
            }
          }
          if (n === 0) return { min: 0, max: 0, mean: 0, energy: 0 };
          const mean = sum / n;
          const energy = sum * intervalHours; // kWh if data already in kW
          return { min: localMin, max: localMax, mean, energy };
        };
        let statsPayload;
        if (hasDual) {
          statsPayload = {
            electrical: computeStats(data1),
            thermal: computeStats(data2)
          };
        } else {
          const seriesData = data1.length ? data1 : data2;
          const singleStats = computeStats(seriesData);
          statsPayload = { electrical: singleStats };
        }
        try { onStats(statsPayload); } catch(_) {}
      }
      if (hasDual) {
        const handleElectricalClick = () => {
          // If electrical is visible
          if (showElectrical) {
            // If thermal also visible, allow hiding electrical -> leave thermal
            if (showThermal) {
              setShowElectrical(false);
            } else {
              // Only electrical visible -> do nothing (can't hide last series)
              return;
            }
          } else {
            // Electrical hidden -> enable it (can result in both visible)
            setShowElectrical(true);
          }
        };
        const handleThermalClick = () => {
          if (showThermal) {
            if (showElectrical) {
              setShowThermal(false);
            } else {
              // Only thermal visible -> prevent hiding last series
              return;
            }
          } else {
            setShowThermal(true);
          }
        };
        legend.select('[data-series="electrical"]').on('click', handleElectricalClick);
        legend.select('[data-series="thermal"]').on('click', handleThermalClick);
      }
    };
    updateLegend(x);

    const seriesRoot = g.append('g').attr('clip-path', `url(#${clipId})`);
    const pointsG = seriesRoot.append('g').attr('class','series-points');
    seriesRoot.append('g').attr('class','series-lines');
    const barsG = seriesRoot.append('g');
    // Draw points only if zoomed in enough (less than 300 points visible)
    function drawPoints(currentX) {
      // Draw separate point sets for electrical & thermal when zoomed sufficiently
      const domain = currentX.domain();
      const leftIndex = d3.bisector(d=>d).left(labels, domain[0]);
      const rightIndex = d3.bisector(d=>d).right(labels, domain[1]);
      const visibleCount = Math.max(1, rightIndex - leftIndex);
      if (visibleCount > 300 || chartType !== 'line') {
        // Remove any existing points if too many or not in line mode
        pointsG.selectAll('circle.point-electrical').remove();
        pointsG.selectAll('circle.point-thermal').remove();
        return;
      }
      // Electrical points
      let electricalPoints = [];
      if (showElectrical && data1.length) {
        electricalPoints = labels.slice(leftIndex, rightIndex).map((t, i) => ({
          series: 'electrical',
          x: currentX(t),
          y: y(data1[leftIndex + i]),
          i: leftIndex + i,
          value: data1[leftIndex + i]
        }));
      }
      // Thermal points
      let thermalPoints = [];
      if (showThermal && data2.length) {
        thermalPoints = labels.slice(leftIndex, rightIndex).map((t, i) => ({
          series: 'thermal',
          x: currentX(t),
          y: y(data2[leftIndex + i]),
          i: leftIndex + i,
          value: data2[leftIndex + i]
        }));
      }
      const electSel = pointsG.selectAll('circle.point-electrical').data(electricalPoints, d => d.i);
      electSel.enter().append('circle')
        .attr('class','point-electrical')
        .attr('r',3)
        .attr('cx', d=>d.x)
        .attr('cy', d=>d.y)
        .attr('fill','#42b4e6')
        .attr('stroke', dark ? '#fff' : '#222')
        .attr('stroke-width',1.5)
        .style('pointer-events','none');
      electSel.exit().remove();
      electSel.attr('cx', d=>d.x).attr('cy', d=>d.y);
      const thermSel = pointsG.selectAll('circle.point-thermal').data(thermalPoints, d => d.i);
      thermSel.enter().append('circle')
        .attr('class','point-thermal')
        .attr('r',3)
        .attr('cx', d=>d.x)
        .attr('cy', d=>d.y)
        .attr('fill','#3dcd58')
        .attr('stroke', dark ? '#fff' : '#222')
        .attr('stroke-width',1.5)
        .style('pointer-events','none');
      thermSel.exit().remove();
      thermSel.attr('cx', d=>d.x).attr('cy', d=>d.y);
    }
    drawPoints(x);
  const { path1, path2, needSimplify, line, lineSimplified, simplifiedLabels } = createSimplifiedPaths({ labels, data1, data2, innerWidth, x, y, name, showElectrical, showThermal, onlyThermal, chartType, rootEl: g.node().parentNode });
  attachRefine({ needSimplify, path1, path2, data1, data2, line, name, showElectrical, showThermal, onlyThermal });

    function renderBarsSVG(scaleX) {
      if (chartType !== 'bar') return;
      barsG.selectAll('*').remove();
  if (!data1.length && !data2.length && !onlyThermal) return;
      const domain = scaleX.domain();
      const leftIndex = d3.bisector(d=>d).left(labels, domain[0]);
      const rightIndex = d3.bisector(d=>d).right(labels, domain[1]);
      const visibleCount = Math.max(1, rightIndex - leftIndex);
      const maxBars = innerWidth; // cap at ~one per pixel
      const stride = Math.max(1, Math.floor(visibleCount / maxBars));
      const indices = [];
      for (let i = leftIndex; i < rightIndex; i += stride) indices.push(i);
      function geom(i){
        const t = labels[i];
        const nextT = (i + stride < labels.length) ? labels[i+stride] : (i>0 ? labels[i] : new Date(t.getTime() + (labels[1]-labels[0]||0)));
        const x0 = scaleX(t);
        const x1 = scaleX(nextT);
        let w = x1 - x0; if (!isFinite(w) || w <= 0) w = 1;
        return {x0, w};
      }
      const prim = indices.filter(i => data1[i] != null && showElectrical);
      barsG.selectAll('rect.bar-primary')
        .data(prim, d=>d)
        .enter().append('rect')
        .attr('class','bar-primary')
        .attr('fill','#42b4e6')
        .attr('x', i => { const {x0,w}=geom(i); return Math.round(x0 - w/2); })
        .attr('y', i => Math.round(y(data1[i])))
        .attr('width', i => { const {w}=geom(i); return Math.max(1, Math.round(w*0.9)); })
        .attr('height', i => Math.max(0, Math.round(height - y(data1[i]))));
      if (hasDual && showThermal) {
        const sec = indices.filter(i => data2[i] != null);
        barsG.selectAll('rect.bar-secondary')
          .data(sec, d=>d)
          .enter().append('rect')
          .attr('class','bar-secondary')
          .attr('fill','#3dcd58')
          .attr('x', i => { const {x0,w}=geom(i); return Math.round(x0 - w*0.25); })
          .attr('y', i => Math.round(y(data2[i])))
          .attr('width', i => { const {w}=geom(i); return Math.max(1, Math.round(w*0.5)); })
          .attr('height', i => Math.max(0, Math.round(height - y(data2[i]))));
      }
      if (onlyThermal && showThermal && !hasDual) {
        const secOnly = indices.filter(i => data2[i] != null);
        barsG.selectAll('rect.bar-thermal-only')
          .data(secOnly, d=>d)
          .enter().append('rect')
          .attr('class','bar-thermal-only')
          .attr('fill','#3dcd58')
          .attr('x', i => { const {x0,w}=geom(i); return Math.round(x0 - w/2); })
          .attr('y', i => Math.round(y(data2[i])))
          .attr('width', i => { const {w}=geom(i); return Math.max(1, Math.round(w*0.9)); })
          .attr('height', i => Math.max(0, Math.round(height - y(data2[i]))));
      }
    }
    if (chartType === 'bar') renderBarsSVG(x);

    // Tooltip & crosshair
    const tooltip = createTooltip(rootEl, dark);
    const crosshair = createCrosshair(g, height, dark);

  const bisectDate = d3.bisector((d) => d).left;
  let lastHoverDate = null; // track last hovered date for data lookups
  let lastHoverX = null;    // track exact pointer x (pixels within plot) to allow smooth motion beyond last data point

    function handleMove(event) {
      const [mxRaw] = d3.pointer(event, g.node());
      if (mxRaw < 0 || mxRaw > innerWidth) { handleLeave(); return; }
      // Clamp raw x strictly to plotting area then use it for crosshair & tooltip (continuous motion)
      const mx = Math.max(0, Math.min(innerWidth, mxRaw));
      const date = xCurrent.invert(mx);
      const idx = bisectDate(labels, date, 1);
      const i = Math.min(labels.length - 1, Math.max(0, idx - 1));
      // Find nearest point to mouse
      let pointX = xCurrent(labels[i]);
      let pointY = null;
      if (hasDual && showElectrical && data1.length) {
        pointY = y(data1[i]);
      } else if (data1.length && showElectrical) {
        pointY = y(data1[i]);
      } else if (onlyThermal && data2.length && showThermal) {
        pointY = y(data2[i]);
      }
      crosshair.attr('x1', pointX).attr('x2', pointX).style('display', null);
      // Highlight the hovered point if dots are visible
      const domain = xCurrent.domain();
      const leftIndex = d3.bisector(d=>d).left(labels, domain[0]);
      const rightIndex = d3.bisector(d=>d).right(labels, domain[1]);
      const visibleCount = Math.max(1, rightIndex - leftIndex);
      if (visibleCount <= 300) {
        pointsG.selectAll('circle.point-electrical')
          .attr('r', d => d.i === i ? 6 : 3)
          .attr('fill', d => d.i === i ? '#eb7857' : '#42b4e6')
          .attr('stroke', d => d.i === i ? '#222' : (dark ? '#fff' : '#222'));
        pointsG.selectAll('circle.point-thermal')
          .attr('r', d => d.i === i ? 6 : 3)
          .attr('fill', d => d.i === i ? '#eb7857' : '#3dcd58')
          .attr('stroke', d => d.i === i ? '#222' : (dark ? '#fff' : '#222'));
      }
      // Tooltip content
      const makeLine = (label, val) => `${label}: ${formatValue(val ?? 0)}`;
      const lines = [];
      if (hasDual) {
        if (showElectrical && data1.length) lines.push(makeLine(tooltipLabels?.electrical ?? t('timeSeries.tooltip.electrical.label'), data1[i]));
        if (showThermal && data2.length) lines.push(makeLine(tooltipLabels?.thermal ?? t('timeSeries.tooltip.thermal.label'), data2[i]));
      } else if (data1.length) {
        if (showElectrical) lines.push(makeLine(tooltipLabels?.generic ?? name ?? t('timeSeries.tooltip.generic.label'), data1[i]));
      } else if (onlyThermal && data2.length && showThermal) {
        lines.push(makeLine(tooltipLabels?.thermal ?? t('timeSeries.tooltip.thermal.label'), data2[i]));
      }
      tooltip
        .html(`<strong>${moment(labels[i]).format("DD-MM-YYYY HH:mm")}</strong><br/>${lines.join('<br/>')}`)
        .style('display', null);
      const topOffset = margin.top + 2;
      tooltip.style('top', `${topOffset}px`);
      // Position tooltip next to the hovered point
      const tooltipNode = tooltip.node();
      let left = pointX + margin.left + 12;
      if (left < margin.left + 2) left = margin.left + 2;
      tooltip.style('left', left + 'px');
      lastHoverDate = labels[i];
      lastHoverX = mx; // store raw x for later re‑position on zoom
    }
    function handleLeave() {
      crosshair.style("display", "none");
      tooltip.style("display", "none");
      pointsG.selectAll('circle.point-electrical')
        .attr('r', 3)
        .attr('fill', '#42b4e6')
        .attr('stroke', dark ? '#fff' : '#222');
      pointsG.selectAll('circle.point-thermal')
        .attr('r', 3)
        .attr('fill', '#3dcd58')
        .attr('stroke', dark ? '#fff' : '#222');
    }
  // We'll attach zoom & mouse events to an invisible overlay rect inside plotting area

    // --- Zoom & Pan (wheel + drag) ---
  // Base (full) domain snapshot + mutable zoom baseline
  const baseX = x.copy();
  let xOriginal = x.copy();
    const zoomedLine = (newX) => d3.line()
      .x((d, i) => newX(labels[i]))
      .y((d) => y(d))
      .defined((d) => typeof d === 'number' && !isNaN(d));

    function redraw(newX, fast=false) {
      xCurrent = newX; // update current scale
      if (chartType === 'line') {
        if (onlyThermal) {
          if (path1) path1.attr('d', zoomedLine(newX)(data2));
          if (path2) path2.style('display','none');
        } else {
          if (path1 && (name !== 'Ashrae' || showElectrical)) path1.attr('d', zoomedLine(newX)(data1));
          if (path2 && (name !== 'Ashrae' || showThermal)) path2.attr('d', zoomedLine(newX)(data2));
        }
        drawPoints(newX);
      } else { renderBarsSVG(newX); }
      if (!fast) {
        xAxisG.call(xAxis.scale(newX))
          .call((sel) => sel.selectAll("text")
            .style("fill", dark ? "#eee" : "#222")
            .style("font-size", axisTickFontSize + "px")
            .style("font-family", 'Nunito, sans-serif'));
        updateLegend(newX);
      }
      // Reposition crosshair & tooltip for the stored hover date (if any) after zoom
      if (lastHoverDate && lastHoverX != null) {
        if (lastHoverDate >= newX.domain()[0] && lastHoverDate <= newX.domain()[1]) {
          // Recompute pixel for stored date; if still in domain adjust crosshair to pointer-relative ratio
          const cx = newX(lastHoverDate);
          crosshair.attr('x1', cx).attr('x2', cx).style('display', null);
          // find index for stats lines
          const idx = bisectDate(labels, lastHoverDate, 1);
          const i = Math.min(labels.length - 1, Math.max(0, idx - 1));
          // Highlight the hovered point if dots are visible
          const domain = newX.domain();
          const leftIndex = d3.bisector(d=>d).left(labels, domain[0]);
          const rightIndex = d3.bisector(d=>d).right(labels, domain[1]);
          const visibleCount = Math.max(1, rightIndex - leftIndex);
          if (visibleCount <= 300) {
            pointsG.selectAll('circle.point-electrical')
              .attr('r', d => d.i === i ? 6 : 3)
              .attr('fill', d => d.i === i ? '#eb7857' : '#42b4e6')
              .attr('stroke', d => d.i === i ? '#222' : (dark ? '#fff' : '#222'));
            pointsG.selectAll('circle.point-thermal')
              .attr('r', d => d.i === i ? 6 : 3)
              .attr('fill', d => d.i === i ? '#eb7857' : '#3dcd58')
              .attr('stroke', d => d.i === i ? '#222' : (dark ? '#fff' : '#222'));
          }
          const lines = [];
          if (hasDual) {
            if (showElectrical && data1.length) lines.push(t('timeSeries.tooltip.electrical', { value: formatValue(data1[i] ?? 0) }));
            if (showThermal && data2.length) lines.push(t('timeSeries.tooltip.thermal', { value: formatValue(data2[i] ?? 0) }));
          } else if (data1.length) {
            if (showElectrical) lines.push(t('timeSeries.tooltip.generic', { name, value: formatValue(data1[i] ?? 0) }));
          } else if (onlyThermal && data2.length && showThermal) {
            lines.push(t('timeSeries.tooltip.thermal', { value: formatValue(data2[i] ?? 0) }));
          }
          tooltip
            .html(`<strong>${moment(labels[i]).format("DD-MM-YYYY HH:mm")}</strong><br/>${lines.join("<br/>")}`)
            .style("display", null);
          const topOffset = margin.top + 2;
          tooltip.style("top", `${topOffset}px`);
          const tooltipNode = tooltip.node();
          let left = cx + margin.left + 12;
          if (left < margin.left + 2) left = margin.left + 2;
          tooltip.style('left', left + 'px');
        } else {
          // If zoomed domain no longer includes lastHoverDate, hide crosshair
          crosshair.style("display", "none");
          tooltip.style("display", "none");
        }
      }
    }

    // --- Minimap (overview) with brush (DEFERRED for performance) ---
  let isBrushing = false;
  let brushFrame = null; // rAF id for throttling
  let pendingBrushDomain = null;
    let isZooming = false;
    let brush = null; let brushG = null; let xMini = null;
    const initMinimap = () => {
      if (!showMinimap || brushG) return; // already built or disabled
      xMini = d3.scaleTime().domain(x.domain()).range([0, innerWidth]);
      const yMini = d3.scaleLinear().domain(y.domain()).range([miniHeight, 0]);
      const miniGroup = g.append("g")
        .attr("class", "minimap")
        .attr("transform", `translate(0, ${height + miniGap})`);
      const miniLine = d3.line()
        .x((d, i) => xMini(labels[i]))
        .y((d) => yMini(d))
        .defined((d) => typeof d === "number" && !isNaN(d));
      miniGroup.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", innerWidth)
        .attr("height", miniHeight)
        .attr("fill", dark ? "#1a1a1a" : "#f5f5f5");
      // Electrical series (when present and not in thermal-only mode) respecting visibility toggle
      if (data1.length && !onlyThermal && showElectrical) {
        miniGroup.append("path")
          .datum(data1)
          .attr("fill","none")
          .attr("stroke", '#42b4e6')
          .attr("stroke-width",0.8)
          .attr("opacity",0.9)
          .attr("d", miniLine);
      }
      // Thermal series: either dual (hasDual) and visible, or thermal-only fallback
      if ((hasDual && data2.length && showThermal) || (onlyThermal && data2.length && showThermal)) {
        miniGroup.append("path")
          .datum(data2)
          .attr("fill","none")
          .attr("stroke", "#3dcd58")
          .attr("stroke-width",0.8)
          .attr("opacity",0.9)
          .attr("d", miniLine);
      }
      brush = d3.brushX().extent([[0,0],[innerWidth, miniHeight]]).on("brush end", brushed);
      brushG = miniGroup.append("g").attr("class","brush").call(brush).call(brush.move, x.range());
      function brushed(event){
        if (isZooming) return;
        if (!event.selection) return;
        const [x0,x1]= event.selection;
        let d0 = xMini.invert(x0); let d1 = xMini.invert(x1);
        // Restrict minimum zoom window to 15 minutes
        const minWindowMs = 15 * 60 * 1000;
        if ((d1 - d0) < minWindowMs) {
          // Center the window around the midpoint
          const mid = new Date((d0.getTime() + d1.getTime()) / 2);
          d0 = new Date(mid.getTime() - minWindowMs / 2);
          d1 = new Date(mid.getTime() + minWindowMs / 2);
          // Clamp to data range
          const domain = x.domain();
          if (d0 < domain[0]) { d0 = domain[0]; d1 = new Date(d0.getTime() + minWindowMs); }
          if (d1 > domain[1]) { d1 = domain[1]; d0 = new Date(d1.getTime() - minWindowMs); }
          // Move brush to restricted window
          brushG.call(brush.move, [d0, d1].map(xMini));
        }
        // Continuous brush -> lightweight redraw throttled with rAF
        if (event.type === 'brush') {
          pendingBrushDomain = [d0,d1];
          if (!brushFrame) {
            brushFrame = requestAnimationFrame(() => {
              isBrushing = true;
              const domain = pendingBrushDomain;
              const newX = x.copy().domain(domain);
              redraw(newX, true); // fast path (skip axis & legend heavy work)
              isBrushing = false;
              brushFrame = null;
            });
          }
        } else if (event.type === 'end') {
          // Finalize: full redraw with axis & legend, update xOriginal baseline
          const newX = x.copy().domain([d0,d1]);
          redraw(newX, false);
          xOriginal = newX.copy();
        }
      }
    };
    // Use requestIdleCallback if available, else fallback to timeout to defer minimap creation
    if (showMinimap) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => initMinimap(), { timeout: 600 });
      } else {
        setTimeout(initMinimap, 120);
      }
    }

    // Wheel (with ctrl/meta) zoom still enabled; drag now used for selection rectangle instead of panning
    const zoom = d3.zoom()
      .filter((event) => {
        if (event.type === 'wheel') return event.ctrlKey || event.metaKey; // modifier required for wheel zoom
        return false; // disable built-in drag panning to reserve drag for selection
      })
      .scaleExtent([1, 500])
      .translateExtent([[0, 0], [innerWidth, height]])
      .extent([[0, 0], [innerWidth, height]])
      .on("zoom", (event) => {
        if (isBrushing) return; // avoid loop
        isZooming = true;
        let newX = event.transform.rescaleX(xOriginal);
        // Restrict minimum zoom window to 15 minutes
        const domain = newX.domain();
        const minWindowMs = 15 * 60 * 1000;
        if ((domain[1] - domain[0]) < minWindowMs) {
          // Center the window around the midpoint
          const mid = new Date((domain[0].getTime() + domain[1].getTime()) / 2);
          let d0 = new Date(mid.getTime() - minWindowMs / 2);
          let d1 = new Date(mid.getTime() + minWindowMs / 2);
          // Clamp to data range
          const baseDomain = x.domain();
          if (d0 < baseDomain[0]) { d0 = baseDomain[0]; d1 = new Date(d0.getTime() + minWindowMs); }
          if (d1 > baseDomain[1]) { d1 = baseDomain[1]; d0 = new Date(d1.getTime() - minWindowMs); }
          newX = x.copy().domain([d0, d1]);
          // Recompute transform so rescaleX(xOriginal) matches the clamped window
          const s = (xOriginal.domain()[1] - xOriginal.domain()[0]) / (d1 - d0);
          const tx = -s * xOriginal(d0);
          d3.select(overlayRef).call(zoom.transform, d3.zoomIdentity.scale(s).translate(tx, 0));
          xOriginal = newX.copy();
        }
        redraw(newX);
        if (showMinimap && brushG && xMini) {
          const domain = newX.domain();
          brushG.call(brush.move, domain.map(xMini));
        }
        isZooming = false;
      });
    zoomBehavior = zoom;
    // Overlay capture rectangle (prevents chart from shifting via transform)
    // Throttled mouse move to reduce handler pressure
    let lastMoveEvt = null; let moveScheduled = false; let lastX = null;
    function throttledMove(e){
      lastMoveEvt = e;
      if (moveScheduled) return;
      moveScheduled = true;
      requestAnimationFrame(()=>{
        moveScheduled = false;
        if (!lastMoveEvt) return;
        const [mx] = d3.pointer(lastMoveEvt, g.node());
        if (lastX !== null && Math.abs(mx - lastX) < 1) return; // skip sub‑pixel
        lastX = mx;
        handleMove(lastMoveEvt);
      });
    }
    let selecting = false; let selStartX = 0; let selectionRect = null;
    function startSelection(event){
      const [mx] = d3.pointer(event, g.node());
      if (mx < 0 || mx > innerWidth) return;
      selecting = true; selStartX = mx;
      // Hide live tooltip/crosshair during selection
      crosshair.style('display','none'); tooltip.style('display','none');
      if (!selectionRect) {
        selectionRect = g.append('rect')
          .attr('class','drag-selection')
          .attr('y',0)
          .attr('height',height)
          .attr('fill','#3399ff')
          .attr('fill-opacity',0.18)
          .attr('stroke','#3399ff')
          .attr('stroke-width',1)
          .attr('pointer-events','none');
      }
      selectionRect.attr('x', selStartX).attr('width', 0).style('display', null);
      d3.select(window).on('mouseup.selection', endSelection);
    }
    function moveSelection(event){
      if (!selecting) { throttledMove(event); return; }
      const [mx] = d3.pointer(event, g.node());
      const clamped = Math.max(0, Math.min(innerWidth, mx));
      const x0 = Math.min(selStartX, clamped);
      const x1 = Math.max(selStartX, clamped);
      selectionRect.attr('x', x0).attr('width', Math.max(1, x1 - x0));
    }
    function endSelection(){
      if (!selecting) return;
      selecting = false;
      if (!selectionRect) return;
      const w = +selectionRect.attr('width');
      if (w < 5) { selectionRect.style('display','none'); return; }
      const x0 = +selectionRect.attr('x');
      const x1 = x0 + w;
      // Convert pixel selection to domain and zoom
      const d0 = xCurrent.invert(x0);
      const d1 = xCurrent.invert(x1);
      if (d1 - d0 > 0) {
        // Enforce minimum 15-minute window for selection zoom
        const minWindowMs = 15 * 60 * 1000;
        let start = d0;
        let end = d1;
        if ((end - start) < minWindowMs) {
          const mid = new Date((start.getTime() + end.getTime()) / 2);
          start = new Date(mid.getTime() - minWindowMs / 2);
          end = new Date(mid.getTime() + minWindowMs / 2);
          const baseDomain = x.domain();
          if (start < baseDomain[0]) { start = baseDomain[0]; end = new Date(start.getTime() + minWindowMs); }
          if (end > baseDomain[1]) { end = baseDomain[1]; start = new Date(end.getTime() - minWindowMs); }
        }
        const newX = x.copy().domain([start,end]);
        redraw(newX);
        xOriginal = newX.copy();
        if (showMinimap && brushG && xMini) {
          brushG.call(brush.move, [start,end].map(xMini));
        }
      }
      selectionRect.style('display','none');
      d3.select(window).on('mouseup.selection', null);
    }
    const overlay = g.append("rect")
      .attr("class", "zoom-overlay")
      .attr("width", innerWidth)
      .attr("height", height)
      .style("fill", "transparent")
      .style("cursor", "crosshair")
      .style("pointer-events", "all")
      .on('mousedown', startSelection)
      .on("mousemove", moveSelection)
      .on("mouseleave", (e)=> { if(!selecting) handleLeave(); });
    overlay.call(zoom);
    overlayRef = overlay.node();

    // One-time small hint (optional) to user about modifier zoom
    if (!rootEl.__wheelHintShown) {
      const hint = d3.select(rootEl).append('div')
        .style('position','absolute')
        .style('bottom','4px')
        .style('right','6px')
        .style('padding','2px 6px')
        .style('font-size','14px')
        .style('font-family','Nunito, sans-serif')
        .style('background', dark ? '#222' : 'rgba(255,255,255,0.85)')
        .style('color', dark ? '#ddd' : '#333')
        .style('border','1px solid ' + (dark ? '#444' : '#ccc'))
        .style('border-radius','3px')
        .style('pointer-events','none')
        .style('opacity',1)
        .text(t('timeSeries.hint.zoom'));
      rootEl.__wheelHintShown = true;
      setTimeout(()=>{ hint.transition().duration(400).style('opacity',0).remove(); }, 3000);
    }

    // Toolbar button actions requiring created elements
    zoomInBtn.on('click', () => { if (zoomBehavior && overlayRef) d3.select(overlayRef).transition().duration(180).call(zoomBehavior.scaleBy, 1.5); });
    zoomOutBtn.on('click', () => {
      if (!overlayRef) return;
      // Compute expanded domain around current visible window, but clamp to full data range
      const baseDomain = x.domain();
      const current = xOriginal.domain();
      const curMs = current[1] - current[0];
      const factor = 1 / 0.67; // inverse of scale used previously
      const newWindowMs = Math.min((baseDomain[1] - baseDomain[0]), curMs * factor);
      const mid = new Date((current[0].getTime() + current[1].getTime()) / 2);
      let d0 = new Date(mid.getTime() - newWindowMs / 2);
      let d1 = new Date(mid.getTime() + newWindowMs / 2);
      // Clamp to base domain
      if (d0 < baseDomain[0]) { d0 = baseDomain[0]; d1 = new Date(d0.getTime() + newWindowMs); }
      if (d1 > baseDomain[1]) { d1 = baseDomain[1]; d0 = new Date(d1.getTime() - newWindowMs); }
      const newX = x.copy().domain([d0, d1]);
      redraw(newX);
      xOriginal = newX.copy();
      // Sync minimap brush
      if (showMinimap && brushG && xMini) {
        brushG.call(brush.move, [d0, d1].map(xMini));
      }
    });
    resetBtn.on('click', () => {
      svg.transition().duration(300).call(zoom.transform, d3.zoomIdentity);
      xOriginal = baseX.copy();
      redraw(baseX);
  if (showMinimap && brushG && brush) brushG.call(brush.move, x.range());
    });

    // Persist references for lightweight width-only adjustment later (without re-running full drawChart)
      try {
        const clipRect = g.select(`#${clipId} rect`).node() ? d3.select(g.select(`#${clipId} rect`).node()) : null;
      chartInternalsRef.current = {
        svg,
        g,
        margin,
        height,
        innerWidth,
        labels,
        data1,
        data2,
        name,
        yAxisName: effectiveYAxisName,
        path1,
        path2,
        barsG,
        chartType,
        onlyThermal,
        showElectrical,
        showThermal,
        x,               // base scale
        xOriginal,        // zoom baseline scale
        xCurrent,         // active scale (may change via zoom / brush)
        y,
        xAxis,
        xAxisG,
        line,
        lineSimplified,
        renderBarsSVG,
        clipRect,
        overlay: overlayRef ? d3.select(overlayRef) : null,
        brushG,
        brush,
        xMini,
        miniGap,
        miniHeight,
        zoomBehavior,
        formatValue,
      };
    } catch(_e) {
      // Swallow; width-only optimization is best-effort
    }

  }, [dataBundle, colorScheme, name, yAxisName, powerUnit, chartType, fullViewport, hideTypeToggle, isFullscreen, showElectrical, showThermal, embedHeight, showMinimapProp, deferUntilVisible]);
  // Update ref each render after drawChart defined
  drawChartRef.current = drawChart;

  // Initial draw only on mount
  // Initial draw: only once when visible
  useEffect(() => {
    if (!isVisible) return;
    if (initializedRef.current) return;
    setIsLoading(true);
    drawChartRef.current();
    initializedRef.current = true;
    setIsLoading(false);
  }, [isVisible]);

  // Compute a lightweight signature of data + visual props that should trigger redraw
  const chartSignature = useMemo(() => {
    const { data1, data2, labels } = dataBundle;
    return computeSignature({ name, yAxisName: effectiveYAxisName, chartType, colorScheme, fullViewport, embedHeight, showMinimapProp, hideTypeToggle, isFullscreen, showElectrical, showThermal, data1, data2, labels, interval });
  }, [dataBundle, name, effectiveYAxisName, chartType, colorScheme, fullViewport, embedHeight, showMinimapProp, hideTypeToggle, isFullscreen, showElectrical, showThermal, interval, powerUnit]);

  // Redraw only when signature changes (and after initial draw)
  const lastSignatureRef = useRef(null);
  // useEffect(() => {
  //   if (!initializedRef.current) return;
  //   if (!isVisible) return;
  //   if (lastSignatureRef.current === chartSignature) return; // unchanged
  //   lastSignatureRef.current = chartSignature;
  //   drawChartRef.current();
  // }, [chartSignature, isVisible]);

  // Initialize lastSignature after first draw so subsequent identical signature renders don't redraw
  useEffect(() => {
    if (!initializedRef.current) return;
    if (lastSignatureRef.current == null) {
      lastSignatureRef.current = chartSignature;
    }
  }, [chartSignature]);

  // Redraw ONLY when the compressed chartSignature actually changes (guards against incidental re-renders)
  useEffect(() => {
    if (!initializedRef.current) return; // skip until first draw done
    if (!isVisible) return; // skip while hidden
    if (layoutTransitioning) return; // skip during layout transitions
    if (lastSignatureRef.current === chartSignature) return; // no meaningful visual/data change
    lastSignatureRef.current = chartSignature; // update stored signature
    // Use ref to avoid depending on drawChart identity (which would retrigger this effect)
    drawChartRef.current();
  }, [chartSignature, isVisible, layoutTransitioning]);

  // Passive listeners to avoid blocking scroll when pointer over chart
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const noop = () => {};
    el.addEventListener('wheel', noop, { passive: true });
    el.addEventListener('touchstart', noop, { passive: true });
    return () => {
      el.removeEventListener('wheel', noop);
      el.removeEventListener('touchstart', noop);
    };
  }, []);

  // ESC to exit internal legacy fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === 'function') onFullscreen();
  }, [onFullscreen]);

  // Removed ResizeObserver to avoid redraws during scroll-triggered layout changes. For responsiveness, a manual refresh or future lightweight resize handler can be added if needed.
  // Responsive: lightweight window resize listener (throttled with rAF) to adapt chart
  useEffect(() => {
    if (!initializedRef.current) return;
    let scheduled = false;
    const attemptResize = () => {
      scheduled = false;
      if (!isVisible) return;
      const rootEl = chartRef.current;
      if (!rootEl) return;
      const newWidth = (isFullscreen || fullViewport) ? window.innerWidth : rootEl.getBoundingClientRect().width;
      const newHeightBase = (isFullscreen || fullViewport) ? window.innerHeight : embedHeight;
      const prev = lastDimsRef.current;
      if (!prev || Math.abs(prev.w - newWidth) > 2 || Math.abs(prev.h - newHeightBase) > 2) {
        lastDimsRef.current = { w: newWidth, h: newHeightBase };
        drawChartRef.current();
      }
    };
    const onResize = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(attemptResize);
    };
    window.addEventListener('resize', onResize, { passive: true });
    attemptResize();
    return () => window.removeEventListener('resize', onResize);
  }, [isFullscreen, fullViewport, embedHeight, isVisible]);
  // Lightweight width-only adjust when side menu toggles: show loader for 500ms, do NOT redraw chart
  const adjustChartWidth = useCallback(() => {
    const internals = chartInternalsRef.current;
    const rootEl = chartRef.current;
    if (!internals || !rootEl) return; // nothing to adjust yet
    const width = (isFullscreen || fullViewport) ? window.innerWidth : rootEl.getBoundingClientRect().width;
    const { margin } = internals;
    const newInnerWidth = width - margin.left - margin.right;
    if (newInnerWidth <= 0 || Math.abs(newInnerWidth - internals.innerWidth) < 2) {
      // trivial or no change; just restore opacity / loader state
      if (internals.svg) internals.svg.attr('width', width);
      // Also update minimap container width if present
      if (internals.xMini && internals.brushG) {
        internals.xMini.range([0, newInnerWidth]);
        // Update minimap background and paths widths
        const miniGroup = internals.brushG.node()?.parentNode;
        if (miniGroup) {
          d3.select(miniGroup).selectAll('rect').attr('width', newInnerWidth);
          d3.select(miniGroup).selectAll('path').attr('d', function(d, i) {
            // Redraw minimap lines for new width
            if (!d) return null;
            const yMini = d3.scaleLinear().domain(internals.y.domain()).range([internals.miniHeight, 0]);
            return d3.line()
              .x((d, i) => internals.xMini(internals.labels[i]))
              .y((d) => yMini(d))
              .defined((d) => typeof d === 'number' && !isNaN(d))(d);
          });
        }
      }
      return;
    }
    // Update stored innerWidth
    internals.innerWidth = newInnerWidth;
    // Update scale ranges
    internals.x.range([0, newInnerWidth]);
    internals.xOriginal.range([0, newInnerWidth]);
    internals.xCurrent.range([0, newInnerWidth]);
    // Update SVG width
    internals.svg && internals.svg.attr('width', width);
    // Update clip rect
    if (internals.clipRect) internals.clipRect.attr('width', newInnerWidth);
    // Update overlay capture rect
    if (internals.overlay) internals.overlay.attr('width', newInnerWidth);
    // Update minimap xMini and minimap group width/paths if present
    if (internals.xMini && internals.brushG) {
      internals.xMini.range([0, newInnerWidth]);
      // Update minimap background and paths widths
      const miniGroup = internals.brushG.node()?.parentNode;
      if (miniGroup) {
        d3.select(miniGroup).selectAll('rect').attr('width', newInnerWidth);
        d3.select(miniGroup).selectAll('path').attr('d', function(d, i) {
          // Redraw minimap lines for new width
          if (!d) return null;
          const yMini = d3.scaleLinear().domain(internals.y.domain()).range([internals.miniHeight, 0]);
          return d3.line()
            .x((d, i) => internals.xMini(internals.labels[i]))
            .y((d) => yMini(d))
            .defined((d) => typeof d === 'number' && !isNaN(d))(d);
        });
      }
    }
    // Recompute line paths or bars using existing data & current domain
    const { chartType, onlyThermal, path1, path2, data1, data2, name, showElectrical, showThermal, y } = internals;
    if (chartType === 'line') {
      const zoomedLine = d3.line()
        .x((d, i) => internals.xCurrent(internals.labels[i]))
        .y(d => y(d))
        .defined(d => typeof d === 'number' && !isNaN(d));
      if (onlyThermal) {
        if (path1) path1.attr('d', zoomedLine(data2));
        if (path2) path2 && path2.style('display','none');
      } else {
        if (path1 && (name !== 'Ashrae' || showElectrical)) path1.attr('d', zoomedLine(data1));
        if (path2 && name === 'Ashrae' && showThermal) path2.attr('d', zoomedLine(data2));
      }
    } else {
      // Bars: reuse existing helper
      if (internals.renderBarsSVG) internals.renderBarsSVG(internals.xCurrent);
    }
    // Update axis (skip legend/stat recomputation for speed)
    if (internals.xAxisG && internals.xAxis) {
      internals.xAxisG.call(internals.xAxis.scale(internals.xCurrent))
        .call(sel => sel.selectAll('text')
          .style('fill', colorScheme === 'dark' ? '#eee' : '#222')
          .style('font-size', '13px')
          .style('font-family', 'Nunito, sans-serif'));
    }
    // Update minimap brush selection if present
    if (internals.brushG && internals.brush && internals.xMini) {
      const domain = internals.xCurrent.domain();
      internals.brushG.call(internals.brush.move, domain.map(internals.xMini));
    }
    // Update zoom behavior extents to new width
    if (internals.zoomBehavior) {
      internals.zoomBehavior
        .extent([[0,0],[newInnerWidth, internals.height]])
        .translateExtent([[0,0],[newInnerWidth, internals.height]]);
      if (internals.overlay) internals.overlay.call(internals.zoomBehavior);
    }
  }, [isFullscreen, fullViewport, colorScheme]);

  // Side menu width-only adjustment handled by reusable component

  // Watch chart container width and force full redraw (teardown + rebuild) when it changes
  useEffect(() => {
    if (!initializedRef.current) return;
    if (!isVisible) return;
    const rootEl = chartRef.current;
    if (!rootEl) return;
    let lastWidth = rootEl.getBoundingClientRect().width;
    let frameId = null;
    function checkWidth() {
      if (!rootEl.isConnected) return;
      const newWidth = rootEl.getBoundingClientRect().width;
      if (Math.abs(newWidth - lastWidth) > 2) {
        lastWidth = newWidth;
        // Teardown and full redraw
        rootEl.innerHTML = '';
        drawChartRef.current();
      }
      frameId = requestAnimationFrame(checkWidth);
    }
    frameId = requestAnimationFrame(checkWidth);
    return () => {
      if (!frameId) return;
      const cancel =
        (typeof cancelAnimationFrame === "function" && cancelAnimationFrame) ||
        (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function"
          ? window.cancelAnimationFrame
          : null);
      if (cancel) cancel(frameId);
    };
  }, [isVisible, collapseSideMenu, isFullscreen, fullViewport, embedHeight]);

  return (
    <>
      <div
        style={{
          backgroundColor: colorScheme === "dark" ? "#111111" : "white",
          width: '100%',
          height: (fullViewport || isFullscreen) ? '100%' : `${embedHeight}px`,
          position: isFullscreen ? 'fixed' : 'relative',
          inset: isFullscreen ? 0 : undefined,
          zIndex: isFullscreen ? 10000 : undefined,
          overflow: isFullscreen ? 'hidden' : undefined,
          transition: 'opacity 200ms ease, transform 200ms ease',
          opacity: 0.9,
          transform: 'scale(1)',
        }}
        className="chart-container"
      >
        <div
          ref={chartRef}
          style={{
            minHeight: `${embedHeight}px`,
            display: chartDisplay,
              position: "relative",
              fontFamily: 'Nunito, sans-serif'
            ,height: (fullViewport || isFullscreen) ? '100%' : undefined,
            width: '100%'
          }}
        />
      </div>
      <ChartWidthAdjuster
        chartRef={chartRef}
        adjustWidth={adjustChartWidth}
        delay={500}
        enabled={initializedRef.current && isVisible}
        setLoading={setIsLoading}
      />
  <se-loading class="se-loading" loading={isLoading}></se-loading>
  <FullscreenModal
    open={fullscreenModal && !inModalInstance}
    onClose={handleCloseModal}
    title={""}
  >
    <TimeSeriesGraph
      dataAssset={dataAssset}
      dataAssset2={dataAssset2}
      name={name}
      yAxisName={powerUnit || yAxisName}
      chartStartDate={chartStartDate}
      fullViewport={false}
      hideTypeToggle={hideTypeToggle}
      showFullscreenButton={true}
      onFullscreen={handleCloseModal}
      embedHeight={Math.max(420, window.innerHeight - 240)}
      showMinimap={true}
      minimapGap={minimapGap}
      deferUntilVisible={false}
      inModalInstance={true}
      interval={interval}
      legendLabels={legendLabelsProp}
      tooltipLabels={tooltipLabels}
    />
  </FullscreenModal>
    </>
  );
};

// React.memo to prevent parent re-renders (unrelated prop changes) from re-running expensive logic
const areEqual = (prev, next) => {
  return (
    prev.name === next.name &&
    prev.yAxisName === next.yAxisName &&
    prev.chartStartDate === next.chartStartDate &&
    prev.fullViewport === next.fullViewport &&
    prev.hideTypeToggle === next.hideTypeToggle &&
    prev.showFullscreenButton === next.showFullscreenButton &&
    prev.embedHeight === next.embedHeight &&
    prev.showMinimap === next.showMinimap &&
    prev.minimapGap === next.minimapGap &&
    prev.deferUntilVisible === next.deferUntilVisible &&
    prev.dataAssset === next.dataAssset &&
    prev.dataAssset2 === next.dataAssset2 &&
    prev.inModalInstance === next.inModalInstance &&
    prev.interval === next.interval
  );
};

export default React.memo(TimeSeriesGraph, areEqual);
